// 3. TimelineFeed Component
// src/components/Timeline/TimelineFeed.tsx
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { format, isSameDay } from 'date-fns';
import TimelineEventCard from './components/TimelineEvent';
import { TimelineEvent, Activity, Accommodation, Transportation } from './types';

interface TimelineFeedProps {
  days: Date[];
  isPastTrip: boolean;
  currentUser?: {
    id: string;
    name: string;
    avatar?: string;
  };
  collaborators?: any[];
  events: TimelineEvent[];
  activities: Activity[];
  accommodations: Accommodation[];
  transportations: Transportation[];
  onAddComment?: (eventId: string, comment: string) => void;
  getEventsForDay: (day: Date) => TimelineEvent[];
  getActivitiesForDay: (day: Date) => Activity[];
  getAccommodationsForDay: (day: Date) => Accommodation[];
  getTransportationForDay: (day: Date) => Transportation[];
  openImageDialog: (images: string[], startIndex?: number) => void;
  onShowCollaborators: () => void;
}

const TimelineFeed: React.FC<TimelineFeedProps> = ({
  days,
  isPastTrip,
  currentUser,
  collaborators = [],
  events,
  activities,
  accommodations,
  transportations,
  onAddComment,
  getEventsForDay,
  getActivitiesForDay,
  getAccommodationsForDay,
  getTransportationForDay,
  openImageDialog,
  onShowCollaborators
}) => {
  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {days.map((day) => {
        const dayEvents = getEventsForDay(day);
        const dayActivities = getActivitiesForDay(day);
        const dayAccommodations = getAccommodationsForDay(day);
        const dayTransportation = getTransportationForDay(day);
        
        // Skip days with no events or activities
        if (dayEvents.length === 0 && dayActivities.length === 0 && 
            dayAccommodations.length === 0 && dayTransportation.length === 0) {
          return null;
        }
        
        const isToday = isSameDay(day, new Date());
        
        return (
          <Box key={day.toISOString()} sx={{ mb: 4 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                mb: 2, 
                pb: 1, 
                borderBottom: '1px solid', 
                borderColor: 'divider' 
              }}
            >
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {isToday && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      py: 0.5, 
                      px: 1, 
                      borderRadius: 1, 
                      mr: 1 
                    }}
                  >
                    Today
                  </Typography>
                )}
                {format(day, 'EEEE, MMMM d, yyyy')}
              </Typography>
            </Box>
            
            {/* Timeline events */}
            {dayEvents.map(event => (
              <TimelineEventCard
                key={event.id}
                event={event}
                isPastTrip={isPastTrip}
                currentUser={currentUser}
                collaborators={collaborators}
                onAddComment={onAddComment}
                onOpenImageDialog={openImageDialog}
                onShowCollaborators={onShowCollaborators}
              />
            ))}
            
            {/* Activities converted to timeline events */}
            {dayActivities.map(activity => {
              const activityEvent: TimelineEvent = {
                id: `activity-${activity.id}`,
                type: 'activity',
                date: activity.date,
                title: activity.title,
                description: activity.description,
                images: activity.images,
                comments: [],
                reference: activity
              };
              return (
                <TimelineEventCard
                  key={activityEvent.id}
                  event={activityEvent}
                  isPastTrip={isPastTrip}
                  currentUser={currentUser}
                  collaborators={collaborators}
                  onAddComment={onAddComment}
                  onOpenImageDialog={openImageDialog}
                  onShowCollaborators={onShowCollaborators}
                />
              );
            })}
            
            {/* Accommodations converted to timeline events */}
            {dayAccommodations.map(accommodation => {
              const accommodationEvent: TimelineEvent = {
                id: `accommodation-${accommodation.id}`,
                type: 'accommodation',
                date: day,
                title: `Stay at ${accommodation.name}`,
                description: accommodation.address,
                images: accommodation.images,
                comments: [],
                reference: accommodation
              };
              return (
                <TimelineEventCard
                  key={accommodationEvent.id}
                  event={accommodationEvent}
                  isPastTrip={isPastTrip}
                  currentUser={currentUser}
                  collaborators={collaborators}
                  onAddComment={onAddComment}
                  onOpenImageDialog={openImageDialog}
                  onShowCollaborators={onShowCollaborators}
                />
              );
            })}
            
            {/* Transportation converted to timeline events */}
            {dayTransportation.map(transport => {
              const transportEvent: TimelineEvent = {
                id: `transport-${transport.id}`,
                type: 'transportation',
                date: new Date(transport.departureTime || day),
                title: `Travel from ${transport.from} to ${transport.to}`,
                description: transport.notes,
                images: transport.images,
                comments: [],
                reference: transport
              };
              return (
                <TimelineEventCard
                  key={transportEvent.id}
                  event={transportEvent}
                  isPastTrip={isPastTrip}
                  currentUser={currentUser}
                  collaborators={collaborators}
                  onAddComment={onAddComment}
                  onOpenImageDialog={openImageDialog}
                  onShowCollaborators={onShowCollaborators}
                />
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

export default TimelineFeed;